import { useState } from 'react';
import { Card } from 'src/components/UI/Card';
import { IconButton } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import TitleTooltip from 'src/components/UI/TitleTooltip';
import { KeywordAssistPageDetail } from '../../types/keyword-assist.type';

interface Props {
  meta: KeywordAssistPageDetail;
}

const KeyIcon = () => (
  <img
    src="/static/images/icons/keyIcon.svg"
    className="w-4 h-4 shrink-0"
    alt="Key"
  />
);

const CopyIcon = () => (
  <img
    src="/static/images/icons/copyIcon.svg"
    className="w-4 h-4 shrink-0 cursor-pointer select-none"
    alt="Copy"
  />
);

const CheckIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth="2.5"
    stroke="#12B76A"
    className="w-4 h-4 shrink-0"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M4.5 12.75l6 6 9-13.5"
    />
  </svg>
);

export default function KAPrimaryKeywordCard({ meta }: Props) {
  const [currentPage, setCurrentPage] = useState(1);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  // Extract secondary keywords list
  const secondaryKeywordsList: string[] = (meta?.secondary_keywords || [])
    .map((item: any) => (typeof item === 'string' ? item : item?.keyword || ''))
    .filter(Boolean);

  const itemsPerPage = 4;
  const secondaryPagesCount = Math.max(
    1,
    Math.ceil(secondaryKeywordsList.length / itemsPerPage)
  );
  const totalPages = 1 + secondaryPagesCount; // Page 1: Primary Keyword, Page 2..N: Secondary Keywords

  const displayPage = totalPages === 0 ? 0 : currentPage;

  const handlePrevPage = () => {
    if (totalPages <= 1) return;
    setCurrentPage((prev) => (prev > 1 ? prev - 1 : totalPages));
  };

  const handleNextPage = () => {
    if (totalPages <= 1) return;
    setCurrentPage((prev) => (prev < totalPages ? prev + 1 : 1));
  };

  const handleCopy = (text: string, index: number) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => {
      setCopiedIndex(null);
    }, 1500);
  };

  // Slice secondary keywords for current sub-page
  const currentSecondaryItems =
    currentPage >= 2
      ? secondaryKeywordsList.slice(
          (currentPage - 2) * itemsPerPage,
          (currentPage - 2) * itemsPerPage + itemsPerPage
        )
      : [];

  return (
    <Card className="flex flex-col justify-between h-full">
      <div className="px-4 sm:px-6">
        {/* Header */}
        <div className="flex justify-between items-start gap-2 mb-3">
          <div className="min-w-0 flex-1">
            <TitleTooltip>
              <TitleTooltip.Text
                title={
                  currentPage === 1 ? 'Primary Keyword' : 'Secondary Keyword'
                }
                className="!text-[#475467] !font-medium !text-base leading-snug"
              >
                <TitleTooltip.Tooltip
                  tooltip={
                    <p className="text-primary text-sm font-medium">
                      {currentPage === 1
                        ? 'Your main target keyword optimized on this page.'
                        : 'These are Orbit suggested secondary keywords, if added in the content can boost the reach.'}
                    </p>
                  }
                />
              </TitleTooltip.Text>
            </TitleTooltip>
          </div>
          <div className="flex items-center gap-2 shrink-0 pt-0.5">
            <span className="text-sm font-medium text-[#475467] whitespace-nowrap">
              {displayPage} / {totalPages}
            </span>
            <div className="flex items-center gap-0.5">
              <IconButton
                onClick={handlePrevPage}
                disabled={totalPages <= 1}
                size="small"
                sx={{ p: 0.5 }}
              >
                <ArrowBackIosNewIcon
                  sx={{
                    fontSize: 14,
                    color: totalPages <= 1 ? '#D0D5DD' : 'var(--color-primary)'
                  }}
                />
              </IconButton>
              <IconButton
                onClick={handleNextPage}
                disabled={totalPages <= 1}
                size="small"
                sx={{ p: 0.5 }}
              >
                <ArrowForwardIosIcon
                  sx={{
                    fontSize: 14,
                    color: totalPages <= 1 ? '#D0D5DD' : 'var(--color-primary)'
                  }}
                />
              </IconButton>
            </div>
          </div>
        </div>

        {/* Content View */}
        {currentPage === 1 ? (
          <div>
            {/* Primary Keyword display */}
            <div className="bg-[#F8F9FC] border border-[#EAECF0] rounded-xl px-4 py-2.5 flex justify-between items-center mb-4 mt-2 min-w-0">
              <span className="text-sm font-semibold text-[#1D2939] truncate min-w-0 mr-2">
                "{meta?.primary_keyword}"
              </span>
              <span className="flex items-center shrink-0">
                <KeyIcon />
              </span>
            </div>
            {/* Metadata Rows */}
            <div>
              <div className="flex justify-between items-center py-2 border-b border-[#F2F4F7]">
                <span className="text-sm text-[#475467] font-medium">
                  Status
                </span>
                <span className="bg-[#F2F4F7] text-[#344054] px-2.5 py-0.5 rounded-lg text-xs font-semibold uppercase tracking-wider">
                  {meta?.status}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-[#F2F4F7]">
                <span className="text-sm text-[#475467] font-medium">
                  Intent
                </span>
                <span className="bg-[#F2F4F7] text-[#344054] px-2.5 py-0.5 rounded-lg text-xs font-semibold uppercase tracking-wider">
                  {meta?.intent}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-[#F2F4F7]">
                <span className="text-sm text-[#475467] font-medium">
                  Intent Aligned
                </span>
                <span
                  className={`px-2.5 py-0.5 rounded-lg text-xs font-semibold ${
                    meta?.intent_aligned
                      ? 'text-[#027A48] bg-[#ECFDF3]'
                      : 'text-[#B42318] bg-[#FEF3F2]'
                  }`}
                >
                  {meta?.intent_aligned ? 'True' : 'False'}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-[#F2F4F7]">
                <span className="text-sm text-[#475467] font-medium">
                  Traffic Potential
                </span>
                <span
                  className={`px-2.5 py-0.5 rounded-lg text-xs font-semibold uppercase tracking-wider ${
                    meta?.traffic_potential === 'high'
                      ? 'text-[#027A48] bg-[#ECFDF3]'
                      : meta?.traffic_potential === 'medium'
                      ? 'text-[#B54708] bg-[#FFFAEB]'
                      : 'text-[#B42318] bg-[#FEF3F2]'
                  }`}
                >
                  {meta?.traffic_potential}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-[#F2F4F7]">
                <span className="text-sm text-[#475467] font-medium">
                  Cannibalization Risk
                </span>
                <span
                  className={`px-2.5 py-0.5 rounded-lg text-xs font-semibold ${
                    !meta?.cannibalization_risk
                      ? 'text-[#344054] bg-[#F2F4F7]'
                      : 'text-[#B42318] bg-[#FEF3F2]'
                  }`}
                >
                  {meta?.cannibalization_risk ? 'True' : 'False'}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <p className="text-sm text-[#475467] mb-2">
              Try these suggested keywords:
            </p>
            {/* Suggestion list */}
            <div className="space-y-2">
              {currentSecondaryItems && currentSecondaryItems.length > 0 ? (
                currentSecondaryItems.map((text, idx) => {
                  const itemIndex = (currentPage - 2) * itemsPerPage + idx;
                  return (
                    <div key={idx} className="flex flex-col">
                      <div className="bg-[#F8F9FC] border border-[#EAECF0] rounded-xl px-3.5 py-2.5 flex justify-between items-center min-w-0 gap-2">
                        <span className="text-sm font-medium text-[#1D2939] truncate min-w-0">
                          "{text}"
                        </span>
                        <button
                          onClick={() => handleCopy(text, itemIndex)}
                          className="flex items-center justify-center shrink-0 w-6 h-6 rounded-md hover:bg-black/5 transition-colors focus:outline-none"
                          title="Copy keyword"
                        >
                          {copiedIndex === itemIndex ? (
                            <CheckIcon />
                          ) : (
                            <CopyIcon />
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="bg-[#F8F9FC] border border-[#EAECF0] rounded-xl px-4 py-6 text-center text-sm font-medium text-[#667085]">
                  No data available
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
